"use client";

import { supabase } from "@/lib/supabase";
import { disarmNotifyOnFinish, isNotifyOnFinishArmed } from "@/lib/notify-on-finish";
import { useEffect, useId, useRef } from "react";

type MessageInsert = {
  id: string;
  chat_id?: string;
  project_chat_id?: string;
  role: string;
  content: string;
};

/**
 * App-shell-level counterpart to the per-chat "response ready" toast in
 * chat-view.tsx's onFinish — that one only fires while the originating
 * ChatView is still mounted, which doesn't cover the case that motivated
 * resumable streams in the first place: the user switched to a different
 * chat, a project, or another part of the app while a turn kept generating.
 *
 * Subscribes once, for the whole session, to INSERTs on messages/
 * project_messages (RLS-scoped — see 20260910000004_enable_realtime_messages.sql)
 * and shows the toast for a completed assistant turn that isn't the chat
 * currently on screen — but only if the user explicitly asked to be told
 * about *this* turn via the "Turn on response notifications" prompt in
 * chat-view.tsx (see notify-on-finish.ts); switching away without opting in
 * stays silent. A chat that IS on screen never toasts here regardless (that
 * one would just be noise over a response the user is already watching).
 *
 * Mount this once, high enough to survive route changes (see
 * global-notifications.tsx, mounted from the root layout) — not in
 * home-client.tsx (unmounts on navigation to /chats, /projects, etc.) and
 * not in ChatView (only ever mounted for the chat currently on screen).
 */
export function useGlobalResponseNotifications({
  userId,
  activeChatId,
  activeProjectChatId,
  onResponseReady,
}: {
  userId: string | null | undefined;
  /** Read via ref internally — passing the latest values on every render
   * without resubscribing the channel on every keystroke/navigation. */
  activeChatId: string | null;
  activeProjectChatId: string | null;
  onResponseReady: (info: { chatId: string; chatName: string; preview: string }) => void;
}) {
  const activeChatIdRef = useRef(activeChatId);
  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  const activeProjectChatIdRef = useRef(activeProjectChatId);
  useEffect(() => {
    activeProjectChatIdRef.current = activeProjectChatId;
  }, [activeProjectChatId]);

  const onResponseReadyRef = useRef(onResponseReady);
  useEffect(() => {
    onResponseReadyRef.current = onResponseReady;
  }, [onResponseReady]);

  // Supabase reuses a channel's underlying connection when the topic string
  // matches one already registered, instead of creating a new one — so a
  // fixed topic here would stack a duplicate listener on every remount (React
  // Strict Mode's double-invoke, a fast-refresh reload, etc.), each firing
  // independently for the same INSERT — the exact "toast repeats N times"
  // bug this caused. Same fix as use-notifications.ts: a unique-per-mount
  // topic via useId().
  const instanceId = useId();

  useEffect(() => {
    if (!userId) return;

    const handleInsert = (
      row: MessageInsert,
      activeIdRef: React.RefObject<string | null>,
      table: "chats" | "project_chats"
    ) => {
      if (row.role !== "assistant") return;
      const chatId = row.chat_id ?? row.project_chat_id;
      if (!chatId) return;
      const kind = table === "chats" ? "chat" : "project";

      if (chatId === activeIdRef.current) {
        // The user is looking at this exact chat right now, so no toast —
        // but this is also the one moment we can mark it "seen" without any
        // timing guesswork: this INSERT fires the instant the row for this
        // turn actually lands, no matter which writer won it (this client's
        // own saveUnsaved, or the original request kept alive server-side
        // via resumable streams across however many navigate-away-and-back
        // cycles). Chat-view.tsx used to guess a "seen" timestamp itself
        // right after watching a turn finish, but the gap between "looks
        // done on screen" and "the winning writer's row actually commits"
        // turned out to be unbounded (observed up to 25s+, scaling with
        // reconnect count) — no fixed buffer could safely cover it. Driving
        // the write off the real persistence event instead removes the
        // guesswork entirely. Project chats have no unseen-dot indicator
        // yet, so only "chats" is written here.
        if (table === "chats") {
          void supabase.from("chats").update({ last_opened_at: "now" }).eq("id", chatId);
        }
        disarmNotifyOnFinish(chatId, kind);
        return;
      }

      // The user only gets a toast for a chat they're not looking at if
      // they explicitly asked for one this turn — see
      // response-notification-prompt.tsx / notify-on-finish.ts. Just
      // switching away without clicking that prompt should stay silent, not
      // notify by default.
      if (!isNotifyOnFinishArmed(chatId, kind)) return;
      disarmNotifyOnFinish(chatId, kind);

      // Fire-and-forget: the row's own INSERT payload doesn't carry the
      // parent chat's name, only its id, so a second lookup is unavoidable
      // to say "chat X is ready" rather than just "a chat is ready".
      void supabase
        .from(table)
        .select("name")
        .eq("id", chatId)
        .maybeSingle()
        .then(({ data }) => {
          onResponseReadyRef.current({
            chatId,
            chatName: data?.name || "Untitled",
            preview: row.content.slice(0, 120),
          });
        });
    };

    let cancelled = false;
    let retryCount = 0;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    let currentChannel: ReturnType<typeof supabase.channel> | undefined;

    // supabase-js's Realtime client doesn't reliably resubscribe a channel
    // on its own after certain disconnects (observed in practice right
    // after a Supabase auth token refresh, which the client reacts to by
    // resetting the underlying socket) — the channel's status callback
    // fires CLOSED and nothing brings it back, silently killing this
    // notification for the rest of the session. Rejoin ourselves whenever
    // that happens, with a fresh topic each time (reusing the old one on a
    // freshly-reconnected socket has been unreliable) and a capped backoff
    // so a genuinely broken connection doesn't retry forever.
    const connect = () => {
      if (cancelled) return;

      // Guards against a genuine, observed failure mode, not just a
      // theoretical one: supabase-js throws synchronously ("cannot add
      // postgres_changes callbacks... after subscribe()") if `.channel()`
      // with a topic that's already subscribed elsewhere returns that
      // existing channel object instead of a fresh one — e.g. React
      // re-running this effect (Strict Mode's double-invoke in dev) before
      // the previous invocation's un-awaited `removeChannel()` cleanup has
      // actually finished on the socket. Left uncaught, that exception
      // aborts this connect() attempt with no channel ever registered and
      // no retry scheduled, silently killing every notification for the
      // rest of the session. Treat it exactly like a dropped connection.
      // removeChannel() unsubscribes, which synchronously re-enters this
      // channel's own status callback with CLOSED — so calling it from inside
      // that callback recurses until the stack blows ("Maximum call stack size
      // exceeded"), taking the whole page down. The retryCount cap below can't
      // stop it: it's only reached *after* the removeChannel() that recurses.
      // Only reproducible while the connection keeps dropping (e.g. the DB
      // being down), which is exactly when this hook must stay alive.
      let torndown = false;

      try {
        const channel = supabase
          .channel(`response-notifications:${userId}:${instanceId}:${retryCount}`)
          .on(
            "postgres_changes",
            { event: "INSERT", schema: "public", table: "messages", filter: `user_id=eq.${userId}` },
            (payload) => handleInsert(payload.new as MessageInsert, activeChatIdRef, "chats")
          )
          .on(
            "postgres_changes",
            { event: "INSERT", schema: "public", table: "project_messages", filter: `user_id=eq.${userId}` },
            (payload) => handleInsert(payload.new as MessageInsert, activeProjectChatIdRef, "project_chats")
          )
          .subscribe((status) => {
            if (cancelled) return;
            if (status === "SUBSCRIBED") {
              retryCount = 0;
              return;
            }
            if (status === "CLOSED" || status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
              if (torndown) return;
              torndown = true;
              void supabase.removeChannel(channel);
              if (retryCount >= 5) return;
              retryCount += 1;
              retryTimeout = setTimeout(connect, Math.min(1000 * 2 ** retryCount, 30_000));
            }
          });

        currentChannel = channel;
      } catch (err) {
        console.error("[useGlobalResponseNotifications] connect failed:", err);
        if (retryCount >= 5) return;
        retryCount += 1;
        retryTimeout = setTimeout(connect, Math.min(1000 * 2 ** retryCount, 30_000));
      }
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(retryTimeout);
      if (currentChannel) void supabase.removeChannel(currentChannel);
    };
  }, [userId, instanceId]);
}
